import { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import { fmtMoney, fmtDateTime, Badge, PAYMENT_METHODS } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { StatCard } from "../components/StatCard";
import { DataTable } from "../components/DataTable";
import { Modal, Field, inputCls, selectCls, btnPrimary } from "../components/Modal";
import { toast } from "sonner";
import { BadgePercent, Coins } from "lucide-react";

export default function Credits() {
  const { user, hasPerm } = useAuth();
  const cur = user?.currency || "FCFA";
  const [data, setData] = useState(null);
  const [settleFor, setSettleFor] = useState(null);
  const [settle, setSettle] = useState({ method: "Espèces", amount: "" });

  const load = () => api.get("/credits").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const doSettle = async () => {
    try {
      await api.post(`/sales/${settleFor.id}/payments`, { method: settle.method, amount: Number(settle.amount) });
      toast.success("Règlement enregistré — solde mis à jour");
      setSettleFor(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <div className="py-20 text-center text-muted-foreground">Chargement...</div>;

  const custCols = [
    { key: "name", label: "Client", render: (c) => <span className="font-semibold">{c.name}</span> },
    { key: "phone", label: "Téléphone" },
    { key: "credit_limit", label: "Limite", className: "text-right", render: (c) => <span className="font-money">{fmtMoney(c.credit_limit, cur)}</span> },
    { key: "balance", label: "Doit", className: "text-right", render: (c) => <span className="font-money font-bold text-rose-600">{fmtMoney(c.balance, cur)}</span> },
  ];

  const saleCols = [
    { key: "number", label: "Facture", render: (s) => <span className="font-money font-bold">{s.number}</span> },
    { key: "created_at", label: "Date", render: (s) => fmtDateTime(s.created_at) },
    { key: "customer_name", label: "Client" },
    { key: "total", label: "Total", className: "text-right", render: (s) => <span className="font-money">{fmtMoney(s.total, cur)}</span> },
    { key: "balance", label: "Reste", className: "text-right", render: (s) => <span className="font-money font-bold text-rose-600">{fmtMoney(s.balance, cur)}</span> },
    { key: "payment_status", label: "Statut", render: (s) => <Badge status={s.payment_status} /> },
    ...(hasPerm("sales.create") ? [{ key: "actions", label: "", className: "text-right", render: (s) => (
      <button data-testid={`settle-credit-${s.id}`} onClick={() => { setSettleFor(s); setSettle({ method: "Espèces", amount: String(s.balance) }); }}
        className="h-8 px-2 inline-flex items-center gap-1 rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950 text-xs font-bold">
        <Coins className="h-4 w-4" /> Encaisser</button>) }] : []),
  ];

  return (
    <div className="space-y-4" data-testid="credits-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Créances Clients</h1>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard testid="credits-total" label="Total des créances" value={fmtMoney(data.total, cur)} icon={BadgePercent} tone="rose" />
        <StatCard testid="credits-customers" label="Clients débiteurs" value={data.customers.length} icon={BadgePercent} tone="amber" />
        <StatCard testid="credits-invoices" label="Factures ouvertes" value={data.open_sales.length} icon={BadgePercent} tone="blue" />
      </div>
      <h2 className="font-heading font-bold text-lg">Par client</h2>
      <DataTable testid="credits-customers-table" columns={custCols} rows={data.customers} searchKeys={["name", "phone"]} empty="Aucune créance" />
      <h2 className="font-heading font-bold text-lg">Factures non soldées</h2>
      <DataTable testid="credits-sales-table" columns={saleCols} rows={data.open_sales} searchKeys={["number", "customer_name"]} empty="Aucune facture ouverte" />

      <Modal open={!!settleFor} onClose={() => setSettleFor(null)} title={`Encaisser — ${settleFor?.number}`}>
        <div className="space-y-3">
          <p className="text-sm">Reste à payer : <b className="font-money">{fmtMoney(settleFor?.balance, cur)}</b></p>
          <Field label="Mode">
            <select data-testid="credit-settle-method" value={settle.method} onChange={(e) => setSettle({ ...settle, method: e.target.value })} className={selectCls}>
              {PAYMENT_METHODS.map((m) => <option key={m}>{m}</option>)}
            </select>
          </Field>
          <Field label="Montant"><input data-testid="credit-settle-amount" type="number" min="0" value={settle.amount} onChange={(e) => setSettle({ ...settle, amount: e.target.value })} className={inputCls} /></Field>
          <button data-testid="credit-settle-confirm" onClick={doSettle} disabled={!Number(settle.amount)} className={`${btnPrimary} w-full`}>Enregistrer</button>
        </div>
      </Modal>
    </div>
  );
}
